import React, { useMemo } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { ArrowLeft, BarChart3, ChevronRight, Cpu, DollarSign, FolderOpen, Layers, PieChart, TrendingUp } from 'lucide-react';
import { CanvasNode, HistoryItem, LightHistoryItem, NodeType, RecentFileMetadata, UserProfile } from '../types';
import { ProfileMenu } from '../components/ProfileMenu';
import { BetaTag, BrandMark } from '../components/BrandMark';
import { nodeKind, type NodeTone } from '../components/nodes/nodeTheme';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { AnalyticsPreset } from '../services/analyticsService';
import { formatRelativeTime, pluralize } from '../utils/workspaceLibrary';

interface AnalyticsPageProps {
  nodes: CanvasNode[];
  history: (HistoryItem | LightHistoryItem)[];
  recentFiles: RecentFileMetadata[];
  onBack: () => void;
  user?: UserProfile | null;
  isPro?: boolean;
  onSubscription?: () => void;
  onSettings?: () => void;
  onSignOut?: () => void;
}

const PRESETS: { key: AnalyticsPreset; label: string }[] = [
  { key: '1year', label: '1 year' },
  { key: '1month', label: '1 month' },
  { key: '1week', label: '1 week' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'custom', label: 'Custom range' },
];

/** A day as these pages write it: 4 Oct 2026 */
const formatDay = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/** When a workspace was last saved, in the words of the library: "Edited 3 h ago" */
const editedAgo = (timestamp?: number) => {
  const ago = formatRelativeTime(timestamp);
  return ago ? `Edited ${ago === 'Just now' ? 'just now' : ago}` : '';
};

/**
 * What a kind of node is called: the name the canvas gives it. The viewer of older workspaces
 * is drawn as a Reference node there, so it is counted as one here.
 */
const kindLabel = (type: NodeType) => (type === NodeType.IMAGE_VIEWER ? 'Reference' : nodeKind(type).label);

export const AnalyticsPage: React.FC<AnalyticsPageProps> = ({ nodes, history, recentFiles, onBack, user, isPro, onSubscription, onSettings, onSignOut }) => {
  const {
    summary,
    preset,
    setPreset,
    customRange,
    setCustomRange,
    rateAssumptions,
    updateRate,
    timeframe,
  } = useAnalyticsData({ nodes, history, recentFiles });

  const sortedModels = useMemo(() => {
    return Object.entries(summary.modelsUsed)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4);
  }, [summary.modelsUsed]);

  // One row per kind as the canvas names it: types that share a name (the three drawn as a Reference) are added up
  const kindRows = useMemo(() => {
    const rows = new Map<string, { label: string; tone: NodeTone; count: number }>();
    (Object.entries(summary.nodeTypeCounts) as [NodeType, number][]).forEach(([type, count]) => {
      if (count <= 0) return;
      const label = kindLabel(type);
      const row = rows.get(label);
      if (row) row.count += count;
      else rows.set(label, { label, tone: nodeKind(type).tone, count });
    });
    return Array.from(rows.values()).sort((a, b) => b.count - a.count);
  }, [summary.nodeTypeCounts]);
  const kindTotal = kindRows.reduce((total, row) => total + row.count, 0);

  const topWorkspaces = summary.topWorkspaces;
  const inRouter = useInRouterContext();

  return (
    <div className="fixed inset-0 z-[300] bg-cream overflow-hidden">
      {/* A column as tall as the window: the bar and the footer stay, the content scrolls between them */}
      <div className="h-full flex flex-col">
        {/* The bar every page has: the mark, the way back, where you are; the account on the right */}
        <header className="ns-pagebar px-4">
          <div className="w-full flex items-center gap-3">
            <span className="flex items-center gap-2.5 shrink-0" aria-hidden="true">
              <BrandMark size={32} />
              <span className="text-[1.0625rem] font-bold tracking-[-0.02em] hidden md:inline">Noodle Studio</span>
              <BetaTag className="hidden md:inline-flex" />
            </span>
            <button
              onClick={onBack}
              className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat group rounded-full"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
              <span>Back to canvas</span>
            </button>
            <span className="ns-t-small hidden sm:flex items-center gap-1.5"><ChevronRight size={12} aria-hidden="true" />Analytics</span>
            <span className="flex-1" />

            <ProfileMenu
              user={user || null}
              isPro={isPro}
              onSubscription={onSubscription}
              onSettings={onSettings}
              onSignOut={onSignOut}
            />
          </div>
        </header>

        {/* Main content: in the gutter and the measure of the bar */}
        <main className="ns-scroll-fade-top flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-8">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Page title */}
            <div className="flex items-center gap-3">
              <span className="ns-mark ns-mark-lavender" aria-hidden="true">
                <BarChart3 size={20} />
              </span>
              <div className="min-w-0">
                <h1 className="ns-t-page">Analytics</h1>
                <p className="text-sm text-ink-soft">Track your workspace activity and estimated costs</p>
              </div>
            </div>

            {/* Time Range Selector */}
            <section className="ns-card">
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-4">
                <h3 className="text-lg font-semibold text-ink">Time range</h3>
                {/* The days the numbers below are counted over */}
                <span className="ns-t-meta">
                  {formatDay(timeframe.start)} – {formatDay(timeframe.end)}
                </span>
              </div>
              {/* One of several: a strip that scrolls sideways where the page is too narrow for all six */}
              <div className="ns-seg max-w-full overflow-x-auto no-scrollbar">
                {PRESETS.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setPreset(item.key)}
                    aria-pressed={preset === item.key}
                    className="ns-seg-item shrink-0"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {preset === 'custom' && (
                <div className="mt-4 flex flex-wrap gap-4">
                  <label className="block">
                    <span className="ns-field-label">Start date</span>
                    <input
                      type="date"
                      value={customRange.start}
                      onChange={(e) => setCustomRange((current) => ({ ...current, start: e.target.value }))}
                      className="ns-field w-auto"
                    />
                  </label>
                  <label className="block">
                    <span className="ns-field-label">End date</span>
                    <input
                      type="date"
                      value={customRange.end}
                      onChange={(e) => setCustomRange((current) => ({ ...current, end: e.target.value }))}
                      className="ns-field w-auto"
                    />
                  </label>
                </div>
              )}
            </section>

            {/* Stats Overview */}
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="ns-card border-lavender bg-lavender-soft">
                <div className="ns-t-label flex items-center justify-between gap-2 text-lavender-deep">
                  Nodes created
                  <Layers size={20} />
                </div>
                <p className="mt-3 text-3xl font-semibold text-lavender-deep">{summary.totalNodes}</p>
                <p className="ns-t-meta mt-1 text-lavender-deep/70">in this period</p>
              </div>

              <div className="ns-card">
                <div className="ns-t-label flex items-center justify-between gap-2">
                  Cost estimate
                  <DollarSign size={20} />
                </div>
                <p className="mt-3 text-3xl font-semibold text-ink">${summary.estimatedCost.toFixed(2)}</p>
                <p className="ns-t-meta mt-1">based on model rates</p>
              </div>

              <div className="ns-card">
                <div className="ns-t-label flex items-center justify-between gap-2">
                  Sessions
                  <TrendingUp size={20} />
                </div>
                <p className="mt-3 text-3xl font-semibold text-ink">{summary.totalSessions}</p>
                <p className="ns-t-meta mt-1">distinct active days</p>
              </div>

              <div className="ns-card">
                <div className="ns-t-label flex items-center justify-between gap-2">
                  Top workspace
                  <PieChart size={20} />
                </div>
                <p className="mt-3 text-lg font-semibold text-ink truncate" title={topWorkspaces[0]?.name}>
                  {topWorkspaces[0]?.name || 'No recent workspace'}
                </p>
                <p className="ns-t-meta mt-1">
                  {editedAgo(topWorkspaces[0]?.lastSaved) || '—'}
                </p>
              </div>
            </section>

            {/* Detailed Breakdowns */}
            <section className="grid gap-6 lg:grid-cols-2">
              {/* Node Type Breakdown */}
              <div className="ns-card">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-ink">Node type breakdown</h3>
                  <span className="ns-chip ns-chip-sm">{pluralize(kindTotal, 'node')}</span>
                </div>
                {kindRows.length > 0 ? (
                  <div className="space-y-3">
                    {kindRows.map((row) => (
                      <div key={row.label} className="flex items-center justify-between gap-4 py-2 border-b border-cream-200 last:border-b-0">
                        <span className="flex items-center gap-2 text-sm text-ink-soft">
                          {/* The colour the kind has on the canvas, so a row is recognised as its node */}
                          <span className="ns-tone-dot" data-tone={row.tone} />
                          {row.label}
                        </span>
                        <span className="text-sm font-medium text-ink">{row.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ns-empty">
                    <span className="ns-mark ns-mark-lg ns-mark-lavender" aria-hidden="true">
                      <Layers size={32} />
                    </span>
                    <p className="ns-empty-title">No nodes in this period</p>
                    <p className="ns-empty-text">Nodes created in this time range are counted here by kind.</p>
                  </div>
                )}
              </div>

              {/* Models Used */}
              <div className="ns-card">
                <h3 className="text-lg font-semibold text-ink mb-4">Models used</h3>
                {sortedModels.length > 0 ? (
                  <div className="space-y-2">
                    {sortedModels.map(([model, hits]) => (
                      <div key={model} className="ns-well flex items-center justify-between gap-3 p-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink truncate" title={model}>{model}</p>
                          <p className="ns-t-small">{pluralize(hits, 'run')}</p>
                        </div>
                        <span className="text-sm font-semibold text-ink shrink-0">
                          ${((rateAssumptions[model] ?? 0) * hits).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ns-empty">
                    <span className="ns-mark ns-mark-lg ns-mark-lavender" aria-hidden="true">
                      <Cpu size={32} />
                    </span>
                    <p className="ns-empty-title">No model usage yet</p>
                    <p className="ns-empty-text">Each generation in this time range is counted under the model that ran it.</p>
                  </div>
                )}
              </div>
            </section>

            {/* Workspace Activity & Cost Settings */}
            <section className="grid gap-6 lg:grid-cols-2">
              {/* Workspace Activity */}
              <div className="ns-card">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-ink">Workspace activity</h3>
                  <span className="ns-t-meta">Top 3</span>
                </div>
                {topWorkspaces.length > 0 ? (
                  <div className="space-y-2">
                    {topWorkspaces.map((ws) => (
                      <div key={ws.name} className="ns-well p-3">
                        <p className="text-sm font-medium text-ink truncate" title={ws.name}>{ws.name}</p>
                        <p className="ns-t-small">
                          {[ws.nodeCount != null ? pluralize(ws.nodeCount, 'node') : '', editedAgo(ws.lastSaved)].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ns-empty">
                    <span className="ns-mark ns-mark-lg ns-mark-lavender" aria-hidden="true">
                      <FolderOpen size={32} />
                    </span>
                    <p className="ns-empty-title">No saved workspaces yet</p>
                    <p className="ns-empty-text">The workspaces you saved last are listed here.</p>
                  </div>
                )}
              </div>

              {/* Cost Assumptions */}
              <div className="ns-card">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-ink">Cost assumptions</h3>
                  <span className="ns-t-meta">Adjust rates</span>
                </div>
                <div className="space-y-2">
                  {['gemini-2.5-flash', 'gemini-3-pro-preview', 'dall-e-3'].map((model) => (
                    <label key={model} className="ns-well flex items-center justify-between gap-3 p-3">
                      <span className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-ink truncate">{model}</span>
                        <span className="ns-t-small">USD per run</span>
                      </span>
                      <input
                        type="number"
                        min={0}
                        step={0.001}
                        value={(rateAssumptions[model] ?? 0).toFixed(3)}
                        onChange={(e) => updateRate(model, Number(e.target.value))}
                        className="ns-field ns-field-sm w-24 shrink-0 text-right"
                      />
                    </label>
                  ))}
                </div>
              </div>
            </section>

            {/* Footer Note */}
            <p className="ns-t-meta text-center pt-2">
              Metrics refresh automatically when you change the timeframe
            </p>
          </div>
        </main>

        {/* Footer: the last line every page has, in the measure of this page */}
        <footer className="py-3 px-4 sm:px-6 border-t border-cream-300 bg-cream/70 backdrop-blur-sm">
          <div className="w-full flex items-center justify-between gap-3">
            <p className="text-ink-soft text-xs font-medium">© 2026 Noodle Studio</p>
            {/* The links go through the app's router; drawn without one (a test, a harness) the line stands alone */}
            {inRouter && (
              <nav aria-label="Footer" className="flex items-center gap-1">
                <Link to="/about" className="ns-btn ns-btn-quiet ns-btn-xs">About</Link>
                <Link to="/terms" className="ns-btn ns-btn-quiet ns-btn-xs">Terms</Link>
                <Link to="/privacy" className="ns-btn ns-btn-quiet ns-btn-xs">Privacy</Link>
              </nav>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};
